import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Droplets, Coffee, Leaf, Info, Activity, Wine } from 'lucide-react';
import { WaterLog, Language, HistoryEntry, CombinedEntry } from '../types';
import { cn } from '../lib/utils';

interface LiquidBreakdownChartProps {
  waterLogs: WaterLog[];
  history?: HistoryEntry[];
  liveMeals?: CombinedEntry[];
  period: 'daily' | 'weekly' | 'monthly' | string;
  lang: Language;
  theme: 'dark' | 'light';
  skin?: string;
  illumination?: boolean;
}

const translations = {
  'en-US': {
    title: 'Liquid Intake Breakdown',
    water: 'Water',
    coffee: 'Coffee',
    tea: 'Tea',
    other: 'Other Drinks',
    total: 'Total Intake',
    dailyAvg: 'Daily Average',
    noData: 'No liquid entries logged for this period.',
    details: 'Beverage Breakdown',
    ml: 'ml',
  },
  'en-GB': {
    title: 'Liquid Intake Breakdown',
    water: 'Water',
    coffee: 'Coffee',
    tea: 'Tea',
    other: 'Other Drinks',
    total: 'Total Intake',
    dailyAvg: 'Daily Average',
    noData: 'No liquid entries logged for this period.',
    details: 'Beverage Breakdown',
    ml: 'ml',
  },
  'ua': {
    title: 'Розподіл рідини за типом',
    water: 'Вода',
    coffee: 'Кава',
    tea: 'Чай',
    other: 'Інші напої',
    total: 'Загальне споживання',
    dailyAvg: 'Середнє за день',
    noData: 'Не знайдено записів про рідину за цей період.',
    details: 'Розподіл напоїв',
    ml: 'мл',
  }
};

const getLogTime = (item: any): number => {
  if (typeof item.timestamp === 'number' && !isNaN(item.timestamp)) return item.timestamp;
  if (item.timestamp && typeof item.timestamp.seconds === 'number') return item.timestamp.seconds * 1000;
  if (typeof item.timestamp === 'string') {
    const parsed = Date.parse(item.timestamp);
    if (!isNaN(parsed)) return parsed;
  }
  if (item.date) {
    const parsed = Date.parse(item.date);
    if (!isNaN(parsed)) return parsed;
  }
  if (item.created_at) {
    const parsed = Date.parse(item.created_at);
    if (!isNaN(parsed)) return parsed;
  }
  return Date.now();
};

export const LiquidBreakdownChart: React.FC<LiquidBreakdownChartProps> = ({
  waterLogs = [],
  history = [],
  liveMeals = [],
  period,
  lang,
  theme,
  skin = 'default',
  illumination = false
}) => {
  const isStalker = skin === 'stalker';
  const tDict = translations[lang] || translations['en-US'];

  const daysInPeriod = useMemo(() => {
    if (period === 'weekly') return 28;
    if (period === 'monthly') return 180;
    return 7;
  }, [period]);

  // Aggregate all liquid intakes across waterLogs, history meals, and liveMeals
  const stats = useMemo(() => {
    const now = new Date();
    const limitDate = new Date(now);

    if (period === 'weekly') {
      limitDate.setDate(limitDate.getDate() - 28);
    } else if (period === 'monthly') {
      limitDate.setMonth(limitDate.getMonth() - 6);
    } else {
      limitDate.setDate(limitDate.getDate() - 7);
    }
    limitDate.setHours(0, 0, 0, 0);

    let water = 0;
    let coffee = 0;
    let tea = 0;
    let other = 0;

    // 1. Explicit Water Logs
    (waterLogs || []).forEach(w => {
      const logTime = getLogTime(w);
      if (logTime >= limitDate.getTime()) {
        const type = w.beverage_type || 'water';
        const amount = Number(w.amount_ml) || 0;
        if (type === 'coffee') coffee += amount;
        else if (type === 'tea') tea += amount;
        else if (type === 'water') water += amount;
        else other += amount;
      }
    });

    // Helper to process meal food items for liquid content
    const processMealFoods = (foods: any[], mealTime: number, fallbackType?: string) => {
      if (mealTime < limitDate.getTime()) return;

      (foods || []).forEach(f => {
        const fname = (f.name || '').toLowerCase();
        const fcat = (f.category || '').toLowerCase();
        const isDrink = fcat === 'drink' || /water|coffee|tea|juice|soda|milk|smoothie|coke|pepsi|fanta|lemonade|ayran|kefir|beer|wine|beverage|drink|вода|кава|чай|напій|сік|молоко/i.test(fname);

        if (isDrink || (f.water_ml && f.water_ml > 0)) {
          const amount = Number(f.water_ml) || Number(f.amount) || 250;
          if (/coffee|кава|espresso|latte|cappuccino|macchiato|americano/i.test(fname) || fallbackType === 'coffee') {
            coffee += amount;
          } else if (/tea|чай|matcha|herbal/i.test(fname) || fallbackType === 'tea') {
            tea += amount;
          } else if (/water|вода|aqua/i.test(fname) || fallbackType === 'water') {
            water += amount;
          } else {
            other += amount;
          }
        }
      });
    };

    // 2. History Meals
    (history || []).forEach(item => {
      const mealTime = getLogTime(item);
      processMealFoods(item.foods || [], mealTime, (item as any).beverage_type);
    });

    // 3. Live Meals (Today)
    (liveMeals || []).forEach(item => {
      if (item.type === 'meal' && item.foods) {
        const mealTime = getLogTime(item);
        processMealFoods(item.foods || [], mealTime, (item as any).beverage_type);
      }
    });

    const total = water + coffee + tea + other;
    return { water, coffee, tea, other, total };
  }, [waterLogs, history, liveMeals, period]);

  const dailyAverage = stats.total / daysInPeriod;

  const chartData = useMemo(() => {
    return [
      { name: tDict.water, value: Math.round(stats.water), color: '#3b82f6', idKey: 'water' },
      { name: tDict.coffee, value: Math.round(stats.coffee), color: '#b45309', idKey: 'coffee' },
      { name: tDict.tea, value: Math.round(stats.tea), color: '#10b981', idKey: 'tea' },
      { name: tDict.other, value: Math.round(stats.other), color: '#8b5cf6', idKey: 'other' }
    ].filter(item => item.value > 0);
  }, [stats, tDict]);

  const hasData = stats.total > 0;

  if (isStalker) {
    return (
      <div 
        id="stalker-liquid-breakdown-panel"
        className="stalker-panel p-4 text-[#4ade80] font-mono border border-[#4ade80]/30 rounded bg-black/85 shadow-lg space-y-4"
      >
        <div className="flex items-center justify-between border-b border-[#4ade80]/30 pb-2 border-dashed">
          <h3 id="stalker-liquid-breakdown-title" className="text-lg stalker-font text-glow text-[#4ade80] uppercase flex items-center gap-2">
            <Droplets className="w-4 h-4" /> LIQUID RAD-DECAY MATRIX
          </h3>
          <span className="text-[10px] text-[#4ade80]/60 uppercase">SCAN: {period}</span>
        </div>

        {!hasData ? (
          <div className="py-8 text-center text-xs text-[#4ade80]/50 uppercase">
            [NO DETECTABLE LIQUID INTAKE PATTERNS RECORDED IN THIS CYCLE]
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            {/* Donut Chart */}
            <div className="h-[140px] w-full relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={58}
                    paddingAngle={chartData.length > 1 ? 4 : 0}
                    dataKey="value"
                    nameKey="name"
                    isAnimationActive={true}
                    animationDuration={900}
                    animationEasing="ease-out"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`stalker-cell-${index}`} fill={entry.color} stroke="#0a0a0a" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(10, 10, 10, 0.95)',
                      border: '1px solid rgba(74, 222, 128, 0.5)',
                      borderRadius: '4px',
                      color: '#4ade80',
                      fontFamily: 'monospace',
                      fontSize: '11px'
                    }}
                    formatter={(value: number) => [`${value} ${tDict.ml}`, '']}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-lg font-bold stalker-font text-glow">{Math.round(stats.total)}</span>
                <span className="text-[8px] text-[#4ade80]/60 uppercase">ML TOTAL</span>
              </div>
            </div>

            {/* List Details */}
            <div className="space-y-2">
              {[
                { label: tDict.water, val: stats.water, pct: stats.total ? (stats.water / stats.total) * 100 : 0, color: 'text-blue-400', barColor: 'bg-blue-500', icon: <Droplets className="w-3.5 h-3.5" /> },
                { label: tDict.coffee, val: stats.coffee, pct: stats.total ? (stats.coffee / stats.total) * 100 : 0, color: 'text-amber-500', barColor: 'bg-amber-600', icon: <Coffee className="w-3.5 h-3.5" /> },
                { label: tDict.tea, val: stats.tea, pct: stats.total ? (stats.tea / stats.total) * 100 : 0, color: 'text-emerald-400', barColor: 'bg-emerald-500', icon: <Leaf className="w-3.5 h-3.5" /> },
                { label: tDict.other, val: stats.other, pct: stats.total ? (stats.other / stats.total) * 100 : 0, color: 'text-purple-400', barColor: 'bg-purple-500', icon: <Wine className="w-3.5 h-3.5" /> }
              ].filter(i => i.val > 0 || stats.total === 0).map((item, index) => (
                <div key={index} className="space-y-1 text-xs">
                  <div className="flex justify-between items-center text-[11px]">
                    <span className={cn("flex items-center gap-1.5 uppercase", item.color)}>
                      {item.icon} {item.label}
                    </span>
                    <span className="text-[#4ade80]/80">
                      {Math.round(item.val)} {tDict.ml} ({Math.round(item.pct)}%)
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#4ade80]/10 border border-[#4ade80]/20 rounded-sm overflow-hidden">
                    <div 
                      className={cn("h-full", item.barColor)} 
                      style={{ width: `${item.pct}%` }} 
                    />
                  </div>
                </div>
              ))}
              <div className="pt-2 text-[10px] text-[#4ade80]/50 uppercase flex justify-between border-t border-[#4ade80]/10 border-dashed">
                <span>{tDict.dailyAvg}:</span>
                <span className="text-glow text-[#4ade80]">{Math.round(dailyAverage)} {tDict.ml}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Standard Mode Component Card
  return (
    <div 
      id="liquid-breakdown-card"
      className={cn(
        "p-6 rounded-[28px] border transition-all flex flex-col space-y-4",
        theme === 'light'
          ? "bg-white border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          : "bg-zinc-900 border-zinc-800 card-shadow",
        illumination ? "illumination-water" : ""
      )}
    >
      <div className="flex items-center justify-between">
        <div className="space-y-1 text-left">
          <h3 id="liquid-breakdown-title" className="text-md font-black uppercase tracking-wider text-zinc-800 dark:text-zinc-200">
            {tDict.title}
          </h3>
          <p className="text-[11px] text-zinc-500 font-bold uppercase tracking-widest">
            {tDict.details}
          </p>
        </div>
        <div className="bg-blue-500/10 p-2.5 rounded-2xl">
          <Activity className="w-5 h-5 text-blue-500" />
        </div>
      </div>

      {!hasData ? (
        <div className="py-12 text-center text-sm text-zinc-400 dark:text-zinc-500 font-semibold flex flex-col items-center justify-center gap-2">
          <Info className="w-5 h-5 text-zinc-400/60" />
          {tDict.noData}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
          {/* Donut Chart visual */}
          <div className="h-[150px] w-full relative flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={62}
                  paddingAngle={chartData.length > 1 ? 4 : 0}
                  dataKey="value"
                  nameKey="name"
                  isAnimationActive={true}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} stroke={theme === 'dark' ? '#18181b' : '#ffffff'} strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    color: theme === 'dark' ? '#f4f4f5' : '#18181b',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                  formatter={(value: number) => [`${value} ${tDict.ml}`, '']}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xl font-black text-zinc-800 dark:text-zinc-100">{Math.round(stats.total)}</span>
              <span className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider">{tDict.ml}</span>
            </div>
          </div>

          {/* Breakdown items list */}
          <div className="space-y-3.5">
            {[
              { label: tDict.water, val: stats.water, pct: stats.total ? (stats.water / stats.total) * 100 : 0, color: 'text-blue-500 dark:text-blue-400', barBg: 'bg-blue-500', icon: <Droplets className="w-4 h-4 text-blue-500" /> },
              { label: tDict.coffee, val: stats.coffee, pct: stats.total ? (stats.coffee / stats.total) * 100 : 0, color: 'text-amber-700 dark:text-amber-500', barBg: 'bg-amber-700', icon: <Coffee className="w-4 h-4 text-amber-700" /> },
              { label: tDict.tea, val: stats.tea, pct: stats.total ? (stats.tea / stats.total) * 100 : 0, color: 'text-emerald-600 dark:text-emerald-400', barBg: 'bg-emerald-500', icon: <Leaf className="w-4 h-4 text-emerald-500" /> },
              { label: tDict.other, val: stats.other, pct: stats.total ? (stats.other / stats.total) * 100 : 0, color: 'text-purple-600 dark:text-purple-400', barBg: 'bg-purple-500', icon: <Wine className="w-4 h-4 text-purple-500" /> }
            ].filter(i => i.val > 0 || stats.total === 0).map((item, index) => (
              <div key={index} className="space-y-1">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    {item.icon}
                    <span className={cn("text-xs font-black uppercase tracking-wider", item.color)}>
                      {item.label}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-zinc-600 dark:text-zinc-400">
                    {Math.round(item.val)} {tDict.ml} <span className="text-zinc-400 dark:text-zinc-500">({Math.round(item.pct)}%)</span>
                  </span>
                </div>
                {/* Visual bar progress */}
                <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className={cn("h-full rounded-full transition-all duration-1000", item.barBg)} 
                    style={{ width: `${item.pct}%` }} 
                  />
                </div>
              </div>
            ))}

            <div className="pt-3 text-[11px] text-zinc-400 dark:text-zinc-500 font-bold uppercase tracking-wider flex justify-between border-t border-zinc-100 dark:border-zinc-800">
              <span>{tDict.dailyAvg}:</span>
              <span className="text-zinc-700 dark:text-zinc-300 font-black">{Math.round(dailyAverage)} {tDict.ml}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
